import React, { useState, useEffect, useRef } from "react";
import MaleIcon   from "../../../../assets/icons/maid-module-icons/male.svg";
import FemaleIcon from "../../../../assets/icons/maid-module-icons/female.svg";
import OthersIcon from "../../../../assets/icons/maid-module-icons/others.svg";
import BikeIcon   from "../../../../assets/icons/maid-module-icons/bike.svg";
import CarIcon    from "../../../../assets/icons/maid-module-icons/car.svg";
import CameraIcon from "../../../../assets/icons/maid-module-icons/Camera.svg";
import ImagePreviewModal from "../modals/ImagePreviewModal";

const Details = ({
  profileImage,
  setProfileImage,
  name,
  setName,
  gender,
  setGender,
  vehicleType,
  setVehicleType,
  vehicleNumber,
  setVehicleNumber,
}) => {
  const genders = [
    { label: "Male",   icon: MaleIcon },
    { label: "Female", icon: FemaleIcon },
    { label: "Other",  icon: OthersIcon },
  ];

  const vehicles = [
    { label: "4W", icon: CarIcon },
    { label: "2W", icon: BikeIcon },
  ];

  const [showCamera, setShowCamera] = useState(false);
  const [cameraError, setCameraError] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  

  // Cleanup camera
  useEffect(() => {
    return () => stopCamera();
  }, []);

    useEffect(() => {
    return () => {
      if (previewUrl && previewUrl.startsWith("blob:")) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
  };

  const startCamera = async () => {
    setCameraError(null);
    stopCamera();

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment" },
        audio: false,
      });

      streamRef.current = stream;

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.playsInline = true;
        videoRef.current.muted = true;
        await videoRef.current.play().catch(err => {
          console.warn("Auto-play failed:", err);
          setCameraError("Could not start camera preview.");
        });
      }

      setShowCamera(true);
    } catch (err) {
      console.error("Camera error:", err);
      let msg = "Cannot access camera.";
      if (err.name === "NotAllowedError") msg = "Camera permission denied.";
      if (err.name === "NotReadableError") msg = "Camera is in use.";
      if (err.name === "NotFoundError") msg = "No camera detected.";
      setCameraError(msg);
    }
  };

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;

    canvas.width = video.videoWidth || 640;
    canvas.height = video.videoHeight || 480;

    canvas.getContext("2d").drawImage(video, 0, 0, canvas.width, canvas.height);

    const dataUrl = canvas.toDataURL("image/jpeg", 0.82);

    fetch(dataUrl)
      .then(res => res.blob())
      .then(blob => {
        const file = new File([blob], `profile-${Date.now()}.jpg`, { type: "image/jpeg" });
        setProfileImage(file);
        
        
        const url = URL.createObjectURL(file);
        setPreviewUrl(url);
        
        setShowCamera(false);
        stopCamera();
      })
      .catch(err => console.error("Capture failed:", err));
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      alert("Please select an image file");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      alert("Image must be smaller than 5MB");
      return;
    }

    setProfileImage(file);
    
    // Create preview URL 
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
  };

  const handleRetake = () => {
    setProfileImage(null);
    if (previewUrl && previewUrl.startsWith("blob:")) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl(null);
  };

  return (
    <div>
      <div style={{ flex: 1, overflowY: "auto", paddingRight: 8 }}
      >
        {/* Profile Photo + Name */}
        <div className="d-flex align-items-start gap-4 mb-5">
          <div style={{ position: "relative", flexShrink: 0 }}>
            {/* Preview */}
            {previewUrl ? (
              <div style={{ position: "relative" }}>
                <img
                  src={previewUrl}
                  alt="Profile preview"
                  style={{
                    width: "120px",
                    height: "120px",
                    borderRadius: "50%",
                    objectFit: "cover",
                    border: "2px solid #E3F2FD",
                    cursor: "pointer",
                  }}
                  onClick={() => setShowModal(true)}
                />
                <button
                  className="btn btn-sm btn-outline-secondary"
                  style={{
                    position: "absolute",
                    bottom: "-30px",
                    left: "50%",
                    transform: "translateX(-50%)",
                    fontSize: "0.75rem",
                    padding: "4px 12px",
                  }}
                  onClick={handleRetake}
                >
                  Retake
                </button>
              </div>
            ) : (
              <div
                style={{
                  width: "120px",
                  height: "120px",
                  borderRadius: "50%",
                  background: "linear-gradient(135deg, #E8F4FD 0%, #F0F8FF 100%)",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  border: "2px solid #E3F2FD",
                  cursor: "pointer",
                }}
                onClick={() => document.getElementById("profile-upload")?.click()}
              >
                <img src={CameraIcon} alt="Camera" style={{ width: "22px", height: "22px" }} />
                <div 
                  style={{ 
                    fontSize: "12px", 
                    color: "#1C80D2", 
                    marginTop: "8px",
                    fontWeight: "500"
                  }}
                >
                  Capture Image
                </div>
              </div>
            )}

            <input
              type="file"
              accept="image/*"
              capture="environment"
              id="profile-upload"
              style={{ display: "none" }}
              onChange={handleFileChange}
            />
          </div>

          {/* Name */}
          <div className="flex-grow-1">
            <label className="form-label fw-bold mb-2">Enter Your Name</label>
            <input
              type="text"
              className="form-control"
              placeholder=""
              value={name}
              onChange={e => setName(e.target.value)}
              style={{ height: 48, fontSize: 16 }}
            />
          </div>
        </div>

        {/* Gender */}
        <div className="mb-5">
          <label className="form-label fw-bold mb-3">Gender</label>
          <div className="d-flex flex-wrap gap-3">
            {genders.map(g => {
              const isActive = gender === g.label;
              return (
                <div
                  key={g.label}
                  onClick={() => {
                    if (isActive) {
                      setGender(null); 
                    } else {
                      setGender(g.label);
                    }
                  }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    padding: "10px 20px",
                    borderRadius: 12,
                    cursor: "pointer",
                    border: isActive ? "2px solid #56B3C3" : "1.5px solid #ced4da",
                    background: isActive ? "#E8FCFF" : "white",
                    transition: "all 0.18s",
                  }}
                >
                  <img src={g.icon} alt={g.label} width={26} height={26} />
                  <span style={{ fontWeight: isActive ? 600 : 500, color: "#6C6C6C" }}>
                    {g.label}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Vehicle Type */}
        <div className="mb-5">
          <label className="form-label fw-bold mb-3">Vehicle Type</label>
          <div className="d-flex gap-3">
            {vehicles.map(v => {
              const isActive = vehicleType === v.label;
              return (
                <div
                  key={v.label}
                  onClick={() => {
                    if (isActive) {
                      setVehicleType(null); 
                    } else {
                      setVehicleType(v.label);
                    }
                  }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    padding: "10px 24px",
                    borderRadius: 12,
                    cursor: "pointer",
                    border: isActive ? "2px solid #56B3C3" : "1.5px solid #ced4da",
                    background: isActive ? "#E8FCFF" : "white",
                    transition: "all 0.18s",
                  }}
                >
                  <img src={v.icon} alt={v.label} width={32} height={24} />
                  <span style={{ fontWeight: isActive ? 600 : 500, color: "#6C6C6C" }}>
                    {v.label}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Vehicle Number */}
        <div className="mb-4">
          <label className="form-label fw-bold mb-2">
            Vehicle Number{" "}
            {!vehicleType && (
              <small className="text-muted">(optional)</small>
            )}
          </label>
          <input
            type="text"
            className="form-control"
            placeholder="e.g. DL 7C AA 1234"
            value={vehicleNumber}
            onChange={e => setVehicleNumber(e.target.value.toUpperCase())}
            style={{ height: 48, fontSize: 16, textTransform: "uppercase" }}
          />
        </div>
      </div>

      {/* Camera Overlay */}
      {showCamera && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.88)",
            zIndex: 2000,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div
            style={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 16,
              background: "#000",
            }}
          >
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              style={{
                width: "100%",
                maxHeight: "75vh",
                objectFit: "cover",
                borderRadius: 16,
              }}
            />
            <canvas ref={canvasRef} style={{ display: "none" }} />
          </div>

          <div
            style={{
              padding: 24,
              background: "#111",
              display: "flex",
              justifyContent: "center",
              gap: 40,
            }}
          >
            <button
              onClick={capturePhoto}
              className="btn btn-success btn-lg px-5 py-3"
              style={{ borderRadius: 50, fontSize: 18 }}
            >
              Capture
            </button>

            <button
              onClick={() => {
                stopCamera();
                setShowCamera(false);
              }}
              className="btn btn-outline-light px-5 py-3"
              style={{ fontSize: 18 }}
            >
              Cancel
            </button>
          </div>

          {cameraError && (
            <div className="alert alert-warning m-4 text-center">{cameraError}</div>
          )}
        </div>
      )}

      {/* Image Preview Modal */}
      <ImagePreviewModal
        show={showModal}
        imageUrl={previewUrl}
        onClose={() => setShowModal(false)}
        title="Profile Photo"
      />
    </div>
  );
};

export default Details;